import React, { useEffect, useRef, useState } from 'react';

import { ProfileModal } from '@/components/ProfileModal';
import { NewMessage } from '@/components/NewMessage';
import { TeamMemberCard } from '@/components/TeamMemberCard';
import { TeamMemberList } from '@/components/TeamMemberList';
import { AsaphButton } from '@/components/shared/Button/AsaphButton';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { Tooltip } from '@material-ui/core';
import { unknownDisplayName, unknownEmail } from '@/config/constants';
import { sentenceCase } from '@/helpers/changeCase';
import { GetChatQuery, useUpdateChatMutation } from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useSnackbar } from 'notistack';

import { IPropsPrivateGroupSettingsDisplay } from '../PrivateGroupSettingsPanel';
import styles from './GroupMembers.module.scss';

export interface GroupMembersProps {
	chat: IPropsPrivateGroupSettingsDisplay['chat'];
	loadChatChannel: IPropsPrivateGroupSettingsDisplay['loadChatChannel'];
}

export const GroupMembers: React.FC<GroupMembersProps> = ({
	chat,
	loadChatChannel,
}) => {
	const { user } = useAuth();
	const [mount, setMount] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [addingGroupMembers, setAddingGroupMembers] =
		useState<boolean>(false);
	const [selectedMemberId, setSelectedMemberId] = useState<string | null>(
		null,
	);
	const lastSelectedMemberIdRef = useRef<string | null>(null);
	if (selectedMemberId !== null) lastSelectedMemberIdRef.current = selectedMemberId;
	const activeMemberId = selectedMemberId ?? lastSelectedMemberIdRef.current;
	const { isLoading: teamLoading, team, refetch } = useTeam();
	const {
		mutateAsync: updateChat,
	} = useUpdateChatMutation<FetchError>();
	const { enqueueSnackbar } = useSnackbar();

	const updateGroupProcessing = false;
	const updateGroupError = null;

	useEffect(() => {
		setLoading(teamLoading || updateGroupProcessing);
	}, [teamLoading, updateGroupProcessing]);

	useEffect(() => {
		if (updateGroupError != null) {
			console.log('Error', updateGroupError);
			enqueueSnackbar(updateGroupError, { variant: 'error' });
		}
	}, [updateGroupError, enqueueSnackbar]);

	const filteredTeamMembers = chat.userIds.reduce<
		{
			roles: string[];
			user: {
				id: string;
				displayName?: string | null;
				email: string;
				lastSeenAt?: string;
				skills?: string[];
			};
		}[]
	>((acc, cuid) => {
		const teamMember = team.members.find((m) => m.user.id === cuid);
		return [
			...acc,
			teamMember || {
				roles: [],
				user: {
					id: cuid,
					displayName: unknownDisplayName,
					email: unknownEmail,
				},
			},
		];
	}, []).sort((a, b) => {
		const aIsSelf = a.user.id === user.id;
		const bIsSelf = b.user.id === user.id;
		if (aIsSelf) return -1;
		if (bIsSelf) return 1;
		const aTime = a.user.lastSeenAt ? new Date(a.user.lastSeenAt).getTime() : 0;
		const bTime = b.user.lastSeenAt ? new Date(b.user.lastSeenAt).getTime() : 0;
		if (bTime !== aTime) return bTime - aTime;
		const aName = a.user.displayName || a.user.email;
		const bName = b.user.displayName || b.user.email;
		return aName.localeCompare(bName);
	});

	const notAddedTeamProfiles = team.members.filter(
		(member) => !chat.userIds.includes(member.user.id),
	);

	const isSelfAdmin =
		team.members.find((m) => m.user.id === user.id)?.roles.includes('teamAdmin') ?? false;

	const removeFromGroup = async (memberId: string) => {
		setSelectedMemberId(null);
		try {
			await updateChat({
				chatId: chat.id,
				userIds: chat.userIds.filter((ui) => ui !== memberId),
			});
			loadChatChannel();
		} catch (err) {
			enqueueSnackbar('Failed to remove member from group', { variant: 'error' });
			loadChatChannel();
		}
	};


	return (
		<>
			<BottomActionLayout
				noPadding
				actionContentClassName={styles['group-members__action-bg']}
				actionContent={
					notAddedTeamProfiles.length > 0 ? (
						<AsaphButton
							color="blue"
							label="Add group members"
							onClick={() => setAddingGroupMembers(true)}
							fullWidth
						/>
					) : (
						<Tooltip title="The whole team is already added to this group">
							<div>
								<AsaphButton
									color="blue"
									label="Add group members"
									fullWidth
									disabled
								/>
							</div>
						</Tooltip>
					)
				}
			>
				<div className={styles['group-members__panel']}>
					<TeamMemberList loading={loading} hideHeader>
						{filteredTeamMembers.map((member) => {
							const isAdmin = member.roles.includes('teamAdmin');
							const isSelf = user.id === member.user.id;

							return (
								<TeamMemberCard
									key={member.user.id}
									id={member.user.id}
									name={
										member.user.displayName ||
										member.user.email
									}
									email={member.user.email}
									role={member.user.skills?.map((s) => sentenceCase(s)).join(', ') || null}
									isSelf={isSelf}
									isAdmin={isAdmin}
									lastActive={null}
									invitePending={false}
									actions={[]}
									onRowPress={() =>
										setSelectedMemberId(member.user.id)
									}
								/>
							);
						})}
					</TeamMemberList>
				</div>
			</BottomActionLayout>
			<NewMessage
				open={addingGroupMembers}
				handleClose={() => {
					loadChatChannel();
					setAddingGroupMembers(false);
				}}
				title="Add group members"
				chat={chat}
				preloadedMembers={notAddedTeamProfiles}
			/>
			<ProfileModal
				memberId={activeMemberId ?? ''}
				open={selectedMemberId !== null}
				onClose={() => setSelectedMemberId(null)}
				onRemoveFromGroup={
					isSelfAdmin && activeMemberId !== user.id
						? () => removeFromGroup(activeMemberId!)
						: undefined
				}
			/>
		</>
	);
};
